import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Save as SaveIcon,
  CheckCircle as VerifiedIcon,
  Warning as WarningIcon,
  Key as KeyIcon
} from '@mui/icons-material';
import ApiService from '../services/api.service';

/**
 * Self-contained AI provider API-key tile for one typing center. Fetches the masked provider status
 * for `typingCenterId`, lets the user set/enable a key, and test the saved connection. Renders one
 * card per supported provider (OpenAI, Anthropic) WITHOUT the "About these keys" help panel — reused by the
 * AI Settings page and the platform Typing Center detail page so the tile stays identical in both.
 */
const AiProviderKeyCard = ({ typingCenterId, canEdit = true, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [providers, setProviders] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [savingProvider, setSavingProvider] = useState(null);
  const [testingProvider, setTestingProvider] = useState(null);
  const [testResults, setTestResults] = useState({});

  const fetchConfig = useCallback(async () => {
    if (!typingCenterId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getAiConfig(typingCenterId);
      const list = response.data?.data?.providers || [];
      setProviders(list);
      // Reset editable drafts: key field always empty (never show the key), enabled from server.
      const nextDrafts = {};
      list.forEach((p) => { nextDrafts[p.provider] = { apiKey: '', enabled: !!p.enabled }; });
      setDrafts(nextDrafts);
      setTestResults({});
    } catch (err) {
      console.error('Error fetching AI config:', err);
      if (err.response?.status !== 404) {
        setError('Failed to load AI configuration');
      }
    } finally {
      setLoading(false);
    }
  }, [typingCenterId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const setDraft = (provider, field, value) => {
    setDrafts((prev) => ({ ...prev, [provider]: { ...prev[provider], [field]: value } }));
  };

  // Test the OpenAI connection using the key currently saved in the config (never the typed one).
  const handleTest = async (provider) => {
    setTestingProvider(provider);
    setTestResults((prev) => ({ ...prev, [provider]: null }));
    try {
      const resp = await ApiService.testAiConnection(typingCenterId, provider);
      setTestResults((prev) => ({ ...prev, [provider]: resp.data?.data || {} }));
    } catch (err) {
      setTestResults((prev) => ({
        ...prev,
        [provider]: { valid: false, reason: err.response?.data?.message || 'Connection test failed.' }
      }));
    } finally {
      setTestingProvider(null);
    }
  };

  const handleSave = async (provider) => {
    setSavingProvider(provider);
    setError(null);
    setSuccess(null);
    try {
      const draft = drafts[provider] || {};
      const dataToSave = { provider, enabled: !!draft.enabled };
      // Only send the key when the user typed one; an empty field keeps the existing key.
      if (draft.apiKey && draft.apiKey.trim()) {
        dataToSave.apiKey = draft.apiKey.trim();
      }
      await ApiService.updateAiConfig(dataToSave, typingCenterId);
      setSuccess('AI configuration saved successfully');
      fetchConfig();
      if (onSaved) onSaved(provider);
    } catch (err) {
      console.error('Error saving AI config:', err);
      setError(err.response?.data?.message || 'Failed to save AI configuration');
    } finally {
      setSavingProvider(null);
    }
  };

  const formatSetBy = (p) => {
    if (!p.updated_by_name) return null;
    let when = '';
    if (p.updated_at) {
      const d = new Date(p.updated_at);
      if (!isNaN(d.getTime())) {
        when = ` · ${d.toLocaleDateString()}`;
      }
    }
    return `Key set by ${p.updated_by_name}${when}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {providers.map((p) => {
        const draft = drafts[p.provider] || { apiKey: '', enabled: false };
        const isSaving = savingProvider === p.provider;
        const isTesting = testingProvider === p.provider;
        const testResult = testResults[p.provider];
        // Flag the card when the key is missing or the provider is disabled — extraction won't run.
        const needsAttention = !p.has_api_key || !draft.enabled;
        const setBy = formatSetBy(p);
        return (
          <Paper
            key={p.provider}
            sx={{
              p: 3,
              mb: 3,
              border: needsAttention ? '1px solid' : undefined,
              borderColor: needsAttention ? 'error.main' : undefined
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight={600}>
                {p.label || p.provider} API Key
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!draft.enabled}
                    disabled={!canEdit}
                    onChange={(e) => setDraft(p.provider, 'enabled', e.target.checked)}
                    color="primary"
                  />
                }
                label={draft.enabled ? 'Enabled' : 'Disabled'}
              />
            </Box>

            {p.has_api_key ? (
              <Alert severity="success" icon={<VerifiedIcon />} sx={{ mb: 2 }}>
                API key configured{p.api_key_last4 ? ` (ending in ••••${p.api_key_last4})` : ''}.
              </Alert>
            ) : (
              <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                No API key configured. Document extraction will not work until you add your own
                {` ${p.label || p.provider} `}API key below.
              </Alert>
            )}

            <TextField
              label={`${p.label || p.provider} API Key`}
              value={draft.apiKey}
              onChange={(e) => setDraft(p.provider, 'apiKey', e.target.value)}
              disabled={!canEdit}
              fullWidth
              type="password"
              placeholder={p.has_api_key ? 'Leave empty to keep existing key' : 'sk-...'}
              helperText="Your key is stored encrypted and used only for your typing center's document extraction."
              InputProps={{ startAdornment: <KeyIcon sx={{ mr: 1, color: 'text.disabled' }} /> }}
            />

            {setBy && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {setBy}
              </Typography>
            )}

            {testResult && (
              <Alert severity={testResult.valid ? 'success' : 'error'} sx={{ mt: 2 }}>
                {testResult.valid
                  ? 'Connection successful — the API key is valid.'
                  : (testResult.reason || 'Connection failed.')}
              </Alert>
            )}

            {canEdit && (
              <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={isTesting ? <CircularProgress size={20} /> : null}
                  onClick={() => handleTest(p.provider)}
                  disabled={isTesting || isSaving || !!(draft.apiKey && draft.apiKey.trim())}
                  title={draft.apiKey && draft.apiKey.trim()
                    ? 'Save the new key before testing — the test checks the saved key.'
                    : undefined}
                >
                  {isTesting ? 'Testing…' : 'Test connection'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  onClick={() => handleSave(p.provider)}
                  disabled={isSaving}
                  sx={{ minWidth: 150 }}
                >
                  {isSaving ? 'Saving...' : 'Save Settings'}
                </Button>
              </Box>
            )}
          </Paper>
        );
      })}
    </Box>
  );
};

export default AiProviderKeyCard;
